import AnimatedIcon, { type IconVariant } from "components/AnimatedIcon"
import ScrollableArea from "components/ScrollableArea"
import gsap from "gsap"
import { fmobile, fresponsive, ftablet } from "library/fullyResponsive"
import useAnimation from "library/useAnimation"
import useMedia from "library/useMedia"
import { useRef } from "react"
import { renderTitle } from "sections/industry/01-Hero"
import styled, { css } from "styled-components"
import colors from "styles/colors"
import textStyles from "styles/text"
import type { HomeFeature } from "types/aliases"
import Card from "./Card"

export default function Section({ feature }: { feature: HomeFeature }) {
	const wrapperRef = useRef<HTMLDivElement | null>(null)
	const cardsRef = useRef<HTMLDivElement | null>(null)
	const blueText = [feature.blueText ?? ""]
	const mobile = useMedia(false, false, false, true)

	useAnimation(() => {
		if (mobile) return
		gsap
			.timeline({
				scrollTrigger: {
					trigger: cardsRef.current,
					start: "bottom bottom",
				},
			})
			.to([cardsRef.current?.children], {
				stagger: 0.35,
				duration: 1,
				opacity: 1,
				ease: "power2.out",
			})
	}, [mobile])

	const cards = feature.cards?.map((card) => (
		<Card key={card?.title} card={card} />
	))

	return (
		<Wrapper ref={wrapperRef}>
			<AnimatedIconWrapper>
				<AnimatedIcon
					variant={feature.animatedIcon as IconVariant}
					small={mobile}
				/>
			</AnimatedIconWrapper>
			<Title>{feature.title}</Title>
			<Description
				$width={feature.descriptionWidth ?? "570px"}
				$mobileWidth={feature.mobileDescriptionWidth ?? ""}
			>
				{feature.description && renderTitle(feature.description, blueText)}
			</Description>
			<Cards ref={cardsRef}>
				{mobile ? (
					<ScrollableArea sidePadding={16}>{cards}</ScrollableArea>
				) : (
					<>{cards}</>
				)}
			</Cards>
		</Wrapper>
	)
}

const Wrapper = styled.div`
	position: relative;

	${fresponsive(css`
		width: 1340px;
		display: flex;
		flex-direction: column;
		gap: 25px;
	`)}

	${ftablet(css`
		width: 916px;
		text-align: center;
		align-items: center;
	`)}

  ${fmobile(css`
		width: 100vw;
		text-align: left;
		align-items: flex-start;
		gap: 20px;
		height: 643px;
	`)}
`

const Title = styled.div`
	${fresponsive(css`
		${textStyles.h3uncapped};
		color: ${colors.charcoal};
		margin: -1px 0 -4px;
		padding-left: 22px;
	`)}

	${ftablet(css`
		padding-left: unset;

		& > :last-of-type() {
			width: 916px;
		}
	`)}

  ${fmobile(css`
		${textStyles.h5};
		padding-left: unset;
		margin: 0 16px;
	`)}
`

const Description = styled.div<{ $width: string; $mobileWidth: string }>`
	${({ $width }) =>
		fresponsive(css`
			${textStyles.p1};
			color: ${colors.grey3};
			max-width: ${$width};
			margin: -5px 0 5px;
			padding-left: 24px;
		`)}

	${ftablet(css`
		padding-left: unset;
		width: 570px;
	`)}

  ${({ $mobileWidth }) =>
		fmobile(css`
			padding-left: unset;
			${textStyles.p2};
			width: ${$mobileWidth};
			margin: 0 16px;
		`)}

  .blue-text {
		${fresponsive(css`
			${textStyles.p1};
			color: ${colors.blue3};
		`)}

		${fmobile(css`
			${textStyles.p2};
		`)}
	}
`

const Cards = styled.div`
	${fresponsive(css`
		width: 100%;
		display: flex;
		gap: 25px;
	`)}

	${ftablet(css`
		width: 885px;
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		grid-template-rows: auto auto;
		justify-items: center;
		text-align: left;

		& > :nth-child(3) {
			grid-column: 1 / -1;
			justify-self: center;
		}
	`)}

  ${fmobile(css`
		width: 100vw;
	`)}
`

const AnimatedIconWrapper = styled.div`
	${fresponsive(css`
		padding-left: 20px;
	`)}

	${ftablet(css`
		padding-left: unset;
	`)}

  ${fmobile(css`
		padding-left: 20px;
	`)}
`
